import type { ReactNode } from 'react';
import { BoutonExcel, BoutonGmail } from './BoutonsIcone';
import { Infobulle } from './Infobulle';

export interface FiltreActif {
  cle: string;
  libelle: string;
  onRetirer: () => void;
}

/**
 * Recherche et filtres en tête d'une liste.
 *
 * Trois choses, toujours dans le même ordre sur les trois écrans :
 * la recherche libre à gauche, les filtres à droite, et sous la barre
 * les filtres ACTIFS en pastilles retirables.
 *
 * Le résumé dit toujours « X sur Y » : sans lui, une liste filtrée ne se
 * distingue pas d'une liste vide, et on cherche une ligne qu'un filtre
 * oublié masque.
 */
export function BarreRecherche({
  recherche,
  onChercher,
  placeholder,
  filtres,
  actifs = [],
  onToutEffacer,
  resume,
  onExporter,
  onEcrire,
}: {
  recherche: string;
  onChercher: (valeur: string) => void;
  placeholder: string;
  /** Les listes déroulantes propres à l'écran. */
  filtres?: ReactNode;
  actifs?: FiltreActif[];
  onToutEffacer?: () => void;
  /** « 34 adhérents sur 321 », montant compris quand il éclaire le filtre. */
  resume?: ReactNode;
  onExporter?: () => void;
  /** « Écrire à la sélection » : la liste affichée, en communication groupée (2026-10-08). */
  onEcrire?: () => void;
}) {
  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-2">
        <input
          className="champ min-w-[16rem] flex-1"
          value={recherche}
          onChange={(evenement) => onChercher(evenement.target.value)}
          placeholder={placeholder}
        />
        {filtres}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-encre-500">
        {actifs.map((filtre) => (
          <Infobulle key={filtre.cle} texte="Retirer ce filtre" className="inline-flex">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-full bg-encre-100 px-2 py-0.5 text-xs text-encre-700 hover:bg-encre-200"
              onClick={filtre.onRetirer}
            >
              {filtre.libelle}
              <span aria-hidden>×</span>
            </button>
          </Infobulle>
        ))}
        {actifs.length > 1 && onToutEffacer && (
          <button type="button" className="text-xs underline" onClick={onToutEffacer}>
            Tout effacer
          </button>
        )}

        {resume && <span className="text-xs">{resume}</span>}

        {/* En icones, aux couleurs des marques (2026-10-08) : le libelle
            vit dans l'infobulle et l'aria-label. */}
        {(onExporter || onEcrire) && (
          <span className="ml-auto flex items-center gap-1">
            {onEcrire && <BoutonGmail libelle="Écrire à la sélection" onClick={onEcrire} />}
            {onExporter && <BoutonExcel libelle="Exporter la liste (Excel)" onClick={onExporter} />}
          </span>
        )}
      </div>
    </div>
  );
}
